import cn from 'classnames';
import { FormikProps } from 'formik';
import React, { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import CancelIcon from '../../../assets/images/icon-cancel-button-2.svg';
import { ReactComponent as CheckmarkIcon } from '../../../assets/images/icon-checkmark.svg';
import DeleteIcon from '../../../assets/images/icon-delete-button.svg';
import { useHeaderDispatch } from '../../../contexts/HeaderInfo';
import useModal from '../../../hooks/useModal';
import useWindowSize from '../../../hooks/useWindowSize';
import {
  FormStatusEnum,
  IMaintenanceFormOutputData,
  MaintenanceFormStateEnum,
  SUBMIT_BUTTON,
} from '../../../types/forms';
import notification from '../../../utils/notification';
import { DeleteItemModal, UnsavedChangesModal } from '../../modals/CommonModals';
import { ActionButton, TextIconButton } from '../buttons';
import styles from './MaintenanceForm.module.scss';

interface IConfigMember {
  label?: string;
  disabled?: boolean;
  action?: (...args: any[]) => void;
  component?: ReactNode;
  hide?: boolean;
  hint?: string;
}

interface IMaintenanceFormConfigProps {
  submit?: IConfigMember;
  header?: IConfigMember;
  delete?: IConfigMember;
  cancel?: IConfigMember;
  actions?: IConfigMember[];
}

interface IProps {
  assetName?: string;
  className?: string;
  controlsClassName?: string;
  children: (data: IMaintenanceFormOutputData) => ReactNode | ReactNode[];
  form: FormikProps<Partial<any>>;
  mode?: MaintenanceFormStateEnum;
  onDelete?: (callback?: () => void) => Promise<void>;
  onCancel?: (...args: any[]) => void;
  onDiscard?: (...args: any[]) => void;
  onSave?: (...args: any[]) => void;
  showControls?: boolean;
  root: string;
  config?: IMaintenanceFormConfigProps;
  isValueChanged: boolean;
  disabled?: boolean; // Used to force disable the form
}

const MaintenanceForm: React.FC<IProps> = ({
  config,
  children,
  className,
  controlsClassName,
  assetName = 'item',
  form,
  mode = MaintenanceFormStateEnum.Adding,
  root,
  showControls = true,
  onDelete,
  onCancel,
  onDiscard,
  onSave,
  isValueChanged,
  disabled = false,
}) => {
  const history = useHistory();

  const { setHeader, setAdditionalData } = useHeaderDispatch();
  const { openModal: openDeleteItemModal, closeModal: closeDeleteItemModal, Modal: DeleteModal } = useModal();
  const { openModal: openSaveChangesModal, closeModal: closeSaveChangesModal, Modal: SaveChangesModal } = useModal();

  const { mobile } = useWindowSize();
  const isAdding = useMemo(() => mode === MaintenanceFormStateEnum.Adding, [mode]);
  const isEditing = useMemo(() => mode === MaintenanceFormStateEnum.Editing, [mode]);
  const isReading = useMemo(() => mode === MaintenanceFormStateEnum.Reading, [mode]);

  const isSubmitSuccess = useMemo(() => form.status === FormStatusEnum.Success, [form.status]);
  const isSubmitFailure = useMemo(() => form.status === FormStatusEnum.Error, [form.status]);
  const apiError = useMemo(() => form?.errors?.api, [form?.errors?.api]);
  const backAction = useMemo(() => {
    return config && config.header ? config.header.action : undefined;
  }, [config?.header]);

  const handleOnDeleteClick = useCallback(() => {
    openDeleteItemModal();
  }, [openDeleteItemModal]);

  const handleOnCancelEditingClick = useCallback(() => {
    if (typeof onCancel === 'function') {
      onCancel();
    }
  }, [onCancel]);

  const renderOptionalControls = useCallback(() => {
    const { delete: deleteConfig, cancel, actions } = config || {};
    return (
      <div className={styles.optionalControls}>
        {!cancel?.hide && isValueChanged && !isSubmitSuccess && (
          <TextIconButton
            trigger={mobile}
            className={styles.textBtn}
            title={cancel?.label ?? 'Cancel editing'}
            onClick={cancel?.action ?? handleOnCancelEditingClick}
            disabled={cancel?.disabled}
            icon={CancelIcon}
            dataAction="cancel_btn"
          />
        )}
        {!deleteConfig?.hide && (
          <TextIconButton
            trigger={mobile}
            className={cn(styles.textBtn, styles.delete)}
            title={deleteConfig?.label ?? `Delete this ${assetName}`}
            onClick={deleteConfig?.action ?? handleOnDeleteClick}
            disabled={deleteConfig?.disabled}
            icon={DeleteIcon}
            dataAction="delete_btn"
          />
        )}
        {actions && actions.length ? actions.map(({ component }: IConfigMember) => component) : null}
      </div>
    );
  }, [isSubmitSuccess, assetName, handleOnDeleteClick, handleOnCancelEditingClick, isValueChanged, config, mobile]);

  const setHeaderInfo = useCallback(() => {
    if (typeof setHeader === 'function') {
      if (isAdding) {
        return setHeader({
          title: config?.header?.label || `Add new ${assetName}`,
          action: backAction || (isValueChanged ? openSaveChangesModal : () => history.push(root)),
        });
      }
      if (isEditing) {
        return setHeader({
          title: config?.header?.label || `Edit ${assetName}`,
          action: backAction || (isValueChanged ? openSaveChangesModal : () => history.push(root)),
        });
      }
      if (isReading) {
        return setHeader({
          title: config?.header?.label || `View ${assetName}`,
          action: backAction ? backAction : () => history.push(root),
        });
      }
    }
  }, [
    config?.header?.label,
    setHeader,
    openSaveChangesModal,
    assetName,
    isAdding,
    isEditing,
    isReading,
    backAction,
    isValueChanged,
  ]);

  const setHeaderAdditionalData = useCallback(() => {
    if (!setAdditionalData) {
      return;
    }

    if (mobile && showControls && isEditing) {
      setAdditionalData(renderOptionalControls());
    } else {
      setAdditionalData();
    }

    // Need to clean additional information  after yourself
    return setAdditionalData;
  }, [setAdditionalData, mobile, renderOptionalControls, showControls, isEditing]);

  const handleOnSaveClick = useCallback(
    (e?: FormEvent) => {
      e?.stopPropagation();
      if (config?.submit?.action) {
        config?.submit?.action();
      } else if (typeof form.handleSubmit === 'function') {
        form.setStatus(FormStatusEnum.Start);
        form.handleSubmit();
      }
    },
    [form],
  );

  const handleDeleteItem = useCallback(() => {
    if (typeof onDelete === 'function') {
      onDelete(closeDeleteItemModal);
    }
  }, [onDelete, closeDeleteItemModal]);

  const handleSaveChanges = useCallback(() => {
    return onSave
      ? () =>
          onSave(form.values, {
            setSubmitting: form.setSubmitting,
            setStatus: form.setStatus,
            setErrors: form.setErrors,
            resetForm: form.resetForm,
          })
      : undefined;
  }, [onSave, form.values, form.resetForm, form.setSubmitting, form.setStatus, form.setErrors]);

  const isSubmitDisabled = useMemo(() => {
    switch (true) {
      case isEditing: {
        return config &&
          config.submit &&
          typeof config.submit.disabled !== 'undefined' &&
          config.submit.disabled !== null
          ? config.submit.disabled
          : !isValueChanged;
      }
      default:
        return false;
    }
  }, [isValueChanged, config?.submit, isEditing]);

  const toasts = useMemo(() => {
    switch (true) {
      case isAdding:
        return {
          success: `The new ${assetName} was added!`,
          failure: apiError ? apiError : `An unexpected error has occurred. The new ${assetName} was not added.`,
        };
      case isEditing:
        return {
          success: `The ${assetName} was edited!`,
          failure: apiError ? apiError : `An unexpected error has occurred. The new ${assetName} was not edited.`,
        };
      default:
        return null;
    }
  }, [isAdding, isEditing, assetName, apiError]);

  const toastsRef = useRef(toasts);

  const saveChangesModalActions = useMemo(() => {
    return [
      ...(onSave
        ? [
            {
              title: 'Save and close',
              onClick: handleSaveChanges(),
            },
          ]
        : []),
      { title: 'Discard changes', onClick: onDiscard, isOutlined: true },
    ];
  }, [onDiscard, onSave, handleSaveChanges]);

  const deleteItemModal = useMemo(
    () => (
      <DeleteModal className="common-modal">
        {() => <DeleteItemModal assetName={assetName} onDelete={handleDeleteItem} onClose={closeDeleteItemModal} />}
      </DeleteModal>
    ),
    [assetName, closeDeleteItemModal, handleDeleteItem, DeleteModal],
  );

  const saveChangesModal = useMemo(
    () => (
      <SaveChangesModal className="common-modal">
        {() => <UnsavedChangesModal actions={saveChangesModalActions} onClose={closeSaveChangesModal} />}
      </SaveChangesModal>
    ),
    [closeSaveChangesModal, saveChangesModalActions, SaveChangesModal],
  );

  useEffect(() => {
    return setHeaderAdditionalData();
  }, [setHeaderAdditionalData]);

  useEffect(() => {
    setHeaderInfo();
  }, [setHeaderInfo]);

  // toasts need a ref to prevent a duplicate toast caused by
  // Formik clearing `form.errors.api` on field focus/blur,
  // which re-triggered the effect while `isSubmitFailure` is still true after the error.
  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  useEffect(() => {
    if (isSubmitSuccess && toastsRef.current?.success) {
      notification.success(void 0, { content: toastsRef.current.success });
    }
    if (isSubmitFailure && toastsRef.current?.failure) {
      notification.error(void 0, { content: toastsRef.current.failure as string });
    }
  }, [isSubmitSuccess, isSubmitFailure]);

  return (
    <>
      {children({ className, mode, root })}
      {showControls && !isReading && (
        <div className={cn(styles.controls, controlsClassName)}>
          {isEditing && (
            <>
              {!mobile && renderOptionalControls()}
              {!config?.submit?.hide && !isSubmitSuccess ? (
                <ActionButton
                  type={SUBMIT_BUTTON}
                  title={config?.submit?.label || 'Save changes'}
                  hint={config?.submit?.hint}
                  disabled={isSubmitDisabled || !form.isValid || disabled}
                  onClick={handleOnSaveClick}
                  className={styles.saveBtn}
                  dataAction="save_btn"
                />
              ) : (
                <div className={cn(styles.status)}>
                  <CheckmarkIcon className={cn(styles.statusIcon)} />
                  Changes are saved
                </div>
              )}
            </>
          )}
          {!config?.submit?.hide && isAdding && (
            <ActionButton
              type={SUBMIT_BUTTON}
              title={config?.submit?.label || `Add new ${assetName}`}
              disabled={form.isSubmitting || !form.isValid || disabled}
              onClick={handleOnSaveClick}
              className={styles.saveBtn}
              dataAction="add_btn"
            />
          )}
        </div>
      )}
      {deleteItemModal}
      {saveChangesModal}
    </>
  );
};

export default MaintenanceForm;
